import { uniqBy } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFilterProvider } from 'modules/finance/components/FinancialWorkspace/Providers/FiltersProvider'
import { FilterValue } from 'modules/finance/components/FinancialWorkspace/types/filter'
import {
  initialState,
  useCustomFilters,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/CustomFilters'

import { DataSourceSelector } from './DataSourceSelector'
import { ProjectsFilter } from './Upsells/Projects'

export const useFilters = () => {
  const { dispatch, formattedState: customFilters } = useCustomFilters()
  const { savedFilters, saveFilters } = useFilterProvider()

  const hasFilter = Object.values(customFilters.employeeCosts || {}).some(
    (filter) => !!filter.length
  )

  const hasSavedFilter = savedFilters.employeeCosts?.some((filter) => !!filter.subItems?.length)

  const filtersApplied = hasSavedFilter || hasFilter

  const filterTracker = useUnsafeMemo(() => {
    return { 'filter:by_data_source': filtersApplied }
  }, [filtersApplied])

  const filters = useUnsafeCallback(
    (recentlyAddedFilter: string | undefined): Record<string, FilterValue> => {
      return {
        dataSourceFilter: {
          component: (
            <DataSourceSelector
              defaultOpen={recentlyAddedFilter === 'dataSourceFilter'}
              defaultSelectedSources={savedFilters.employeeCosts}
              onChange={(value, groupedValues) => {
                dispatch({
                  type: 'SET_EMPLOYEE_COSTS',
                  payload: {
                    agreement_data: groupedValues.agreement_data
                      .map((i) => String(i.subId))
                      .filter((key) => key === 'compensations' || key === 'salary'),
                    employee_expenses: uniqBy(groupedValues.employee_expenses, 'subId').map((i) =>
                      String(i.subId)
                    ),
                    labor_costs: groupedValues.labor_costs.map((i) => String(i.subId)),
                  },
                })

                saveFilters('employeeCosts', value || [])
              }}
            />
          ),
          label: i18n.t('finance.financial_workspace.employee_costs.data_source_selector.title'),
          value: 'dataSourceFilter',
          grouped: false,
          key: 'employeeCosts',
          onReset: () =>
            dispatch({ type: 'SET_EMPLOYEE_COSTS', payload: initialState.employeeCosts }),
        },
        projectsFilter: {
          component: <ProjectsFilter />,
          label: i18n.t('finance.financial_workspace.employee_costs.header.title'),
          value: 'projectsFilter',
          grouped: false,
          position: 10,
          key: 'projects',
        },
      }
    },
    [dispatch, savedFilters, saveFilters]
  )

  return { filtersApplied, filterTracker, filters }
}
